import { Skeleton, Tabs, TabsProps } from 'antd';
import React, {
    ReactElement,
    Suspense,
    useEffect,
    useMemo,
    useState,
    useContext,
} from 'react';
import { useHistory, useParams } from 'react-router';
import CcxComponentProps from '../../../core/CcxComponent';
import { DeploymentsContext } from '../../../core/context/DeploymentsContext';
import Firewalls from '../../firewalls/Firewalls';
import LazyLoader from '../../LazyLoader';
import QueryStats from '../../queryStats/QueryStats';
import styles from './DataStore.module.less';
import DataStoreOverview from './overview/DataStoreOverview';
import ProjectBreadcrumb from '../ProjectBreadcrumb';
import DataStoreSettings from './settings/DataStoreSettings';
import DataStoreServices from './services/DataStoreServices';
import DeploymentDashboard from '../../deployments/dashboard/DeploymentDashboard';
import { Button } from 'antd';
import { Row } from 'antd';
import { Col } from 'antd';
import { hideHeader } from '../../../core/CcxEnv';
import NotFoundError404 from '../../errors/NotFoundError404';
import useThrowError from '../../errors/useThrowError';
import DbDatabases from '../../dbUsers/DbDatabase';
import DbUsers from '../../dbUsers/DbUsers';
import Backups from '../../backups/Backups';
import DataStoreScaling from './DatastoreScaling';

interface UrlProps {
    activeTab: string;
    dataStoreUuid: string;
    projectUuid: string;
}

interface Props extends CcxComponentProps {}

function DataStore({ testId = 'DataStore' }: Props): ReactElement {
    const { throwError } = useThrowError();
    let history = useHistory();
    let { activeTab, dataStoreUuid, projectUuid } = useParams<UrlProps>();
    const [activeKey, setActiveKey] = useState<string>('overview');
    const [dbCount, setDbCount] = useState<number>(0);

    const { deployments } = useContext(DeploymentsContext);

    const deployment = useMemo(
        () => deployments?.getByUuid(dataStoreUuid),
        [deployments, dataStoreUuid]
    );

    useEffect(() => {
        if (deployments && !deployment) {
            throwError(new NotFoundError404('Datastore not found'));
        }
    }, [deployments, deployment]);

    useEffect(() => {
        setActiveKey(activeTab);
    }, [activeTab]);

    const cancel = () => {
        history.push({
            pathname: '/',
        });
    };

    const items: TabsProps['items'] = [
        {
            key: 'overview',
            label: `Overview`,
            children: (
                <Suspense fallback={<LazyLoader />}>
                    <DataStoreOverview />
                </Suspense>
            ),
        },
        {
            key: 'services',
            label: `Nodes`,
            children: (
                <Suspense fallback={<LazyLoader />}>
                    <DeploymentsContext.Consumer>
                        {(provider) => (
                            <DataStoreServices
                                dataStores={provider?.deployments}
                                setDbCount={setDbCount}
                                refreshStores={provider?.refresh!}
                            />
                        )}
                    </DeploymentsContext.Consumer>
                </Suspense>
            ),
        },
        deployment && {
            key: 'monitoring',
            label: `Monitoring`,
            children: (
                <Suspense fallback={<LazyLoader />}>
                    <DeploymentDashboard currentDeployment={deployment} />
                </Suspense>
            ),
        },
        deployment &&
            !deployment.isRedis() && {
                key: 'queryStats',
                label: `Query Stats`,
                children: (
                    <Suspense fallback={<LazyLoader />}>
                        <QueryStats />
                    </Suspense>
                ),
            },
        deployment &&
            !deployment.isRedis() && {
                key: 'dbUsers',
                label: `Users`,
                children: (
                    <DeploymentsContext.Consumer>
                        {(provider) => {
                            return (
                                <Suspense fallback={<LazyLoader />}>
                                    <DbUsers
                                        deployments={provider?.deployments}
                                        onUpdate={provider?.refresh}
                                    />
                                </Suspense>
                            );
                        }}
                    </DeploymentsContext.Consumer>
                ),
            },
        deployment &&
            !deployment.isRedis() && {
                key: 'dbDatabases',
                label: `Databases`,
                children: (
                    <DeploymentsContext.Consumer>
                        {(provider) => {
                            return (
                                <Suspense fallback={<LazyLoader />}>
                                    <DbDatabases
                                        deployments={provider?.deployments}
                                    />
                                </Suspense>
                            );
                        }}
                    </DeploymentsContext.Consumer>
                ),
            },
        {
            key: 'backups',
            label: `Backups`,
            children: (
                <Suspense fallback={<LazyLoader />}>
                    <Backups />
                </Suspense>
            ),
        },
        {
            key: 'firewall',
            label: `Firewall`,
            children: (
                <Suspense fallback={<LazyLoader />}>
                    <Firewalls />
                </Suspense>
            ),
        },
        {
            key: 'settings',
            label: `Settings`,
            children: (
                <Suspense fallback={<LazyLoader />}>
                    <DeploymentsContext.Consumer>
                        {(provider) => (
                            <DataStoreSettings
                                dataStores={provider?.deployments}
                                refresh={provider?.refresh}
                            />
                        )}
                    </DeploymentsContext.Consumer>
                </Suspense>
            ),
        },
    ].filter((item) => !!item) as TabsProps['items'];

    return (
        <section className={styles.DataStore} data-testid={testId}>
            <DeploymentsContext.Consumer>
                {(provider) => (
                    <ProjectBreadcrumb
                        deployments={provider?.deployments}
                        testId={`${testId}Breadcrumb`}
                    />
                )}
            </DeploymentsContext.Consumer>

            <Tabs
                className={styles.DataStoreTabs}
                data-testid={`${testId}Tabs`}
                type="card"
                activeKey={activeKey}
                defaultActiveKey={activeTab}
                destroyInactiveTabPane={true}
                onChange={(tabKey: any) => {
                    history.push(
                        `/projects/${projectUuid}/data-stores/${dataStoreUuid}/${tabKey}`
                    );
                }}
                items={items}
            />

            {hideHeader && (
                <Row className={styles.DataStoreBackRow}>
                    <Col span={2} offset={22}>
                        <Button onClick={() => cancel()}>Back</Button>
                    </Col>
                </Row>
            )}
        </section>
    );
}

export default DataStore;
